import React, { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../contexts/AuthContext'
import { arrayToCSV } from '../../utils/security'
import { supabase } from '../../lib/supabase'
import { queryKeys } from '../../lib/queryClient'
import { Surface, Button, ToggleButton, ToggleButtonGroup, Table, Skeleton } from '@heroui/react'
import AffiliateHeader from '../../components/affiliate/AffiliateHeader'
import AreaChartWidget from '../../components/charts/AreaChartWidget'
import BarChartWidget from '../../components/charts/BarChartWidget'
import { useChartPalette, formatCurrency } from '../../components/charts/chartUtils'
import TrendingUp from '~icons/solar/graph-up-bold'
import UserPlus from '~icons/solar/user-plus-bold'
import ShoppingCart from '~icons/solar/cart-large-bold'
import Download from '~icons/solar/download-bold'
import Activity from '~icons/solar/pulse-bold'

type TimeRange = '7d' | '30d' | '90d' | 'all'

interface AnalyticsData {
  earningsOverTime: { date: string; earnings: number; commissions: number }[]
  conversionFunnel: { stage: string; count: number; percentage: number }[]
  trafficSources: { source: string; clicks: number; conversions: number }[]
  topPerformers: { product: string; sales: number; earnings: number }[]
  dailyMetrics: { date: string; clicks: number; signups: number; purchases: number }[]
}

const EMPTY_ANALYTICS: AnalyticsData = {
  earningsOverTime: [],
  conversionFunnel: [
    { stage: 'Clicks', count: 0, percentage: 100 },
    { stage: 'Signups', count: 0, percentage: 0 },
    { stage: 'Purchases', count: 0, percentage: 0 }
  ],
  trafficSources: [],
  topPerformers: [],
  dailyMetrics: []
}

// Parses a click's referrer into a traffic-source label. Kept client-side
// (rather than folded into the get_affiliate_analytics RPC) since this is
// already-tested URL-parsing logic with several fallback branches --
// re-implementing it in SQL risks subtly diverging on edge cases
// (malformed URLs, non-http referrers, missing referrers).
function referrerSource(referrer: string | null): string {
  if (!referrer || referrer === 'direct') return 'Direct'
  try {
    if (referrer.startsWith('http://') || referrer.startsWith('https://')) {
      return new URL(referrer).hostname
    }
    return referrer
  } catch {
    return referrer || 'Unknown'
  }
}

export default function AnalyticsPage() {
  const { user } = useAuth()
  const [timeRange, setTimeRange] = useState<TimeRange>('30d')
  const palette = useChartPalette()

  const affiliateCode = user?.affiliateCode
  const affiliateProgramId = user?.affiliateProgramId
  const hasIdentity = !!affiliateCode && !!affiliateProgramId

  const startDate = useMemo(() => {
    if (timeRange === 'all') return new Date('2020-01-01')
    const now = new Date()
    const d = new Date()
    d.setDate(now.getDate() - (timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90))
    return d
  }, [timeRange])

  const analyticsQuery = useQuery({
    queryKey: queryKeys.analytics(affiliateProgramId ?? '', timeRange),
    enabled: hasIdentity,
    queryFn: async (): Promise<AnalyticsData> => {
      const { data, error } = await supabase.rpc('get_affiliate_analytics', {
        p_affiliate_program_id: affiliateProgramId,
        p_affiliate_code: affiliateCode,
        p_start_date: startDate.toISOString(),
      })
      if (error) throw error

      const totalClicks = data.totalClicks ?? 0
      const totalSignups = data.totalSignups ?? 0
      const totalConversions = data.totalConversions ?? 0

      const trafficMap = new Map<string, { clicks: number; conversions: number }>()
      ;(data.clicksForTraffic as { referrer: string | null }[] | null)?.forEach((click) => {
        const source = referrerSource(click.referrer)
        const current = trafficMap.get(source) || { clicks: 0, conversions: 0 }
        trafficMap.set(source, { ...current, clicks: current.clicks + 1 })
      })

      const trafficSources = Array.from(trafficMap.entries())
        .map(([source, d]) => ({ source, ...d }))
        .sort((a, b) => b.clicks - a.clicks)
        .slice(0, 10)

      return {
        earningsOverTime: data.earningsOverTime ?? [],
        conversionFunnel: [
          { stage: 'Clicks', count: totalClicks, percentage: 100 },
          { stage: 'Signups', count: totalSignups, percentage: totalClicks > 0 ? (totalSignups / totalClicks) * 100 : 0 },
          { stage: 'Purchases', count: totalConversions, percentage: totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0 }
        ],
        trafficSources,
        // TODO: needs product/plan data from metadata
        topPerformers: [],
        dailyMetrics: data.dailyMetrics ?? []
      }
    },
  })

  const loading = hasIdentity && analyticsQuery.isLoading
  const analytics = analyticsQuery.data ?? EMPTY_ANALYTICS

  const exportData = () => {
    if (!analytics) return

    // SECURITY: Use sanitized CSV export to prevent CSV injection
    const headers = ['Date', 'Clicks', 'Signups', 'Purchases']
    const rows = analytics.dailyMetrics.map(m => [
      m.date,
      m.clicks,
      m.signups,
      m.purchases
    ])
    const csv = arrayToCSV(headers, rows)

    // Download CSV
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `analytics-${timeRange}-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const trafficColors = [palette.accent, palette.success, palette.warning, palette.danger, palette.muted]

  const totalClicks = analytics.conversionFunnel[0].count
  const totalSignups = analytics.conversionFunnel[1].count
  const totalPurchases = analytics.conversionFunnel[2].count
  const conversionRate = totalClicks > 0 ? ((totalPurchases / totalClicks) * 100).toFixed(1) : '0.0'

  const metrics = [
    { label: 'Total Clicks', value: totalClicks.toLocaleString(), icon: Activity, color: 'text-accent' },
    { label: 'Signups', value: totalSignups.toLocaleString(), icon: UserPlus, color: 'text-accent' },
    { label: 'Purchases', value: totalPurchases.toLocaleString(), icon: ShoppingCart, color: 'text-success' },
    { label: 'Conversion Rate', value: `${conversionRate}%`, icon: TrendingUp, color: 'text-warning' },
  ]

  // The page shell (title, Export, time-range toggle) stays mounted and
  // interactive the whole time -- switching timeRange starts a new query
  // (it's part of the queryKey), which used to flip the entire page back
  // to a full skeleton including these action elements. Only the data
  // sections below swap to a skeleton while `loading` is true.
  return (
    <div className="min-h-screen bg-background">
      <AffiliateHeader />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4 sm:py-8">
        {/* Time Range Selector */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <h2 className="text-2xl font-bold text-foreground">Performance Overview</h2>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <Button variant="outline" onPress={exportData}>
              <Download className="h-4 w-4" />
              Export
            </Button>
            <ToggleButtonGroup
              selectionMode="single"
              disallowEmptySelection
              selectedKeys={new Set([timeRange])}
              onSelectionChange={(keys) => {
                const [first] = Array.from(keys as Set<string>)
                if (first) setTimeRange(first as TimeRange)
              }}
              className="max-w-full"
            >
              <ToggleButton id="7d" style={{ '--toggle-button-fg': 'var(--default-foreground)' } as React.CSSProperties}>7 Days</ToggleButton>
              <ToggleButton id="30d" style={{ '--toggle-button-fg': 'var(--default-foreground)' } as React.CSSProperties}>
                <ToggleButtonGroup.Separator />
                30 Days
              </ToggleButton>
              <ToggleButton id="90d" style={{ '--toggle-button-fg': 'var(--default-foreground)' } as React.CSSProperties}>
                <ToggleButtonGroup.Separator />
                90 Days
              </ToggleButton>
              <ToggleButton id="all" style={{ '--toggle-button-fg': 'var(--default-foreground)' } as React.CSSProperties}>
                <ToggleButtonGroup.Separator />
                All Time
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {loading
            ? [1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)
            : metrics.map((metric) => (
                <Surface key={metric.label} variant="default" className="rounded-xl p-6">
                  <div className="p-2.5 bg-surface-secondary rounded-xl w-fit mb-4">
                    <metric.icon className={`h-5 w-5 ${metric.color}`} />
                  </div>
                  <p className="text-sm text-muted mb-1">{metric.label}</p>
                  <p className="text-2xl font-bold text-foreground">{metric.value}</p>
                </Surface>
              ))
          }
        </div>

        {/* Earnings Over Time */}
        {/* Earnings ($) and commission count (#) are different-unit measures
            -- plotting both against one y-axis would flatten whichever has
            the smaller scale (typically the count), so each gets its own
            chart instead of a shared combo axis. */}
        {loading ? (
          <Skeleton className="h-[21.5rem] rounded-xl mb-8" />
        ) : (
          <div className="mb-8">
            <AreaChartWidget
              title="Earnings Over Time"
              data={analytics.earningsOverTime}
              xKey="date"
              series={[{ key: 'earnings', name: 'Earnings', color: palette.success, valueFormatter: formatCurrency }]}
            />
          </div>
        )}

        {/* Commissions Over Time */}
        {loading ? (
          <Skeleton className="h-64 rounded-xl mb-8" />
        ) : (
          <div className="mb-8">
            <BarChartWidget
              title="Commissions Over Time"
              data={analytics.earningsOverTime}
              xKey="date"
              height={200}
              series={[{ key: 'commissions', name: 'Commissions (#)', color: palette.accent }]}
            />
          </div>
        )}

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {loading ? (
            <>
              <Skeleton className="h-64 rounded-xl" />
              <Skeleton className="h-64 rounded-xl" />
            </>
          ) : (
            <>
              {/* Conversion Funnel */}
              <Surface variant="default" className="rounded-xl p-6">
                <h3 className="text-lg font-semibold text-foreground mb-6">Conversion Funnel</h3>
                <div className="space-y-4">
                  {analytics.conversionFunnel.map((stage, index) => (
                    <div key={stage.stage}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-foreground">{stage.stage}</span>
                        <span className="text-sm text-muted">
                          {stage.count.toLocaleString()} ({stage.percentage.toFixed(0)}%)
                        </span>
                      </div>
                      <div className="w-full bg-surface-secondary rounded-full h-3">
                        <div
                          className={`h-3 rounded-full ${
                            index === 0 ? 'bg-accent' : index === 1 ? 'bg-accent' : 'bg-success'
                          }`}
                          style={{ width: `${stage.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </Surface>

              {/* Traffic Sources */}
              <Surface variant="default" className="rounded-xl p-6">
                <h3 className="text-lg font-semibold text-foreground mb-6">Traffic Sources</h3>
                <div className="space-y-3">
                  {analytics.trafficSources.length === 0 ? (
                    <p className="text-sm text-muted">No traffic data yet</p>
                  ) : (
                    analytics.trafficSources.map((source, index) => (
                      <Surface key={source.source} variant="secondary" className="flex items-center justify-between p-3 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: trafficColors[index % trafficColors.length] }}
                          />
                          <span className="font-medium text-foreground">{source.source}</span>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-foreground">{source.clicks} clicks</p>
                          <p className="text-xs text-muted">{source.conversions} conversions</p>
                        </div>
                      </Surface>
                    ))
                  )}
                </div>
              </Surface>
            </>
          )}
        </div>

        {/* Daily Activity */}
        {loading ? (
          <Skeleton className="h-[25.5rem] rounded-xl" />
        ) : (
          <BarChartWidget
            title="Daily Activity"
            data={analytics.dailyMetrics}
            xKey="date"
            height={300}
            dateAxis={false}
            series={[
              { key: 'clicks', name: 'Clicks', color: palette.accent },
              { key: 'signups', name: 'Signups', color: palette.warning },
              { key: 'purchases', name: 'Purchases', color: palette.success },
            ]}
          />
        )}

        {/* Top Performing Products - Only show if data exists */}
        {!loading && analytics.topPerformers.length > 0 && (
          <Surface variant="default" className="rounded-xl overflow-hidden mt-8">
            <h3 className="text-lg font-semibold text-foreground p-6 pb-0">Top Performing Products</h3>
            <div className="p-6">
              <Surface variant="secondary" className="rounded-2xl overflow-hidden">
                <Table>
                  <Table.ScrollContainer>
                    <Table.Content aria-label="Top performing products" className="min-w-full">
                      <Table.Header>
                        <Table.Column isRowHeader>Product</Table.Column>
                        <Table.Column className="text-right">Sales</Table.Column>
                        <Table.Column className="text-right">Earnings</Table.Column>
                      </Table.Header>
                      <Table.Body>
                        {analytics.topPerformers.map((product) => (
                          <Table.Row key={product.product}>
                            <Table.Cell>
                              <span className="text-sm font-medium text-foreground">{product.product}</span>
                            </Table.Cell>
                            <Table.Cell>
                              <span className="text-sm text-muted block text-right">{product.sales}</span>
                            </Table.Cell>
                            <Table.Cell>
                              <span className="text-sm font-semibold text-success block text-right">
                                ${product.earnings.toLocaleString()}
                              </span>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Content>
                  </Table.ScrollContainer>
                </Table>
              </Surface>
            </div>
          </Surface>
        )}
      </main>
    </div>
  )
}
